const encoder = new TextEncoder();
const decoder = new TextDecoder();

export const PBKDF2_ITERATIONS = 600_000;
export const KDF_VERSION = "pbkdf2-sha256-v1" as const;
export const CRYPTO_VERSION = "aes-gcm-v1" as const;
const saltBytes = 16;
const ivBytes = 12;

type EncryptedApiKey = {
  cipherText: string;
  iv: string;
  kdfVersion: typeof KDF_VERSION;
  cryptoVersion: typeof CRYPTO_VERSION;
  iterations: number;
};

function requireWebCrypto(): SubtleCrypto {
  if (!globalThis.crypto?.subtle) {
    throw new Error("Web Crypto is not available in this browser context.");
  }
  return globalThis.crypto.subtle;
}

function toBase64(bytes: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)));
}

function fromBase64(value: string): Uint8Array {
  return Uint8Array.from(atob(value), (char) => char.charCodeAt(0));
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer as ArrayBuffer;
}

export async function deriveNonExtractableAesKey(
  passphrase: string,
  salt: Uint8Array,
): Promise<CryptoKey> {
  const subtle = requireWebCrypto();
  const material = await subtle.importKey(
    "raw",
    toArrayBuffer(encoder.encode(passphrase)),
    "PBKDF2",
    false,
    ["deriveKey"],
  );

  return subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: toArrayBuffer(salt),
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

export async function encryptApiKey(apiKey: string, passphrase: string): Promise<EncryptedApiKey> {
  if (!apiKey.trim()) {
    throw new Error("API key is required.");
  }

  if (passphrase.length < 12) {
    throw new Error("Passphrase must be at least 12 characters.");
  }

  const iv = crypto.getRandomValues(new Uint8Array(ivBytes));
  const salt = crypto.getRandomValues(new Uint8Array(saltBytes));
  const key = await deriveNonExtractableAesKey(passphrase, salt);
  const encrypted = await requireWebCrypto().encrypt(
    { name: "AES-GCM", iv: toArrayBuffer(iv) },
    key,
    toArrayBuffer(encoder.encode(apiKey)),
  );
  const combined = new Uint8Array(salt.byteLength + encrypted.byteLength);
  combined.set(salt, 0);
  combined.set(new Uint8Array(encrypted), salt.byteLength);

  return {
    cipherText: toBase64(toArrayBuffer(combined)),
    iv: toBase64(toArrayBuffer(iv)),
    kdfVersion: KDF_VERSION,
    cryptoVersion: CRYPTO_VERSION,
    iterations: PBKDF2_ITERATIONS,
  };
}

export async function decryptApiKey(
  cipherText: string,
  iv: string,
  passphrase: string,
): Promise<string> {
  if (passphrase.length < 12) {
    throw new Error("Passphrase must be at least 12 characters.");
  }

  const combined = fromBase64(cipherText);
  const salt = combined.slice(0, saltBytes);
  const encrypted = combined.slice(saltBytes);
  const key = await deriveNonExtractableAesKey(passphrase, salt);
  const decrypted = await requireWebCrypto().decrypt(
    { name: "AES-GCM", iv: toArrayBuffer(fromBase64(iv)) },
    key,
    toArrayBuffer(encrypted),
  );
  return decoder.decode(decrypted);
}

export async function verifyEncryptedApiKey(
  cipherText: string,
  iv: string,
  passphrase: string,
): Promise<boolean> {
  try {
    const plainText = await decryptApiKey(cipherText, iv, passphrase);
    return plainText.trim().length > 0;
  } catch {
    return false;
  }
}
